<template>
  <el-dialog
    title="Import Result Details"
    :visible.sync="dialogVisible"
    width="95%"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @close="handleClose"
    custom-class="import-result-dialog">

    <!-- Summary Section -->
    <el-card class="summary-card" shadow="never">
      <div slot="header" class="clearfix">
        <span class="summary-title">Import Summary</span>
        <div style="float: right;">
          <el-tag v-if="result.failedCount === 0" type="success" size="medium">
            <i class="el-icon-circle-check"></i> All Successful
          </el-tag>
          <el-tag v-else-if="result.successCount === 0" type="danger" size="medium">
            <i class="el-icon-circle-close"></i> All Failed
          </el-tag>
          <el-tag v-else type="warning" size="medium">
            <i class="el-icon-warning"></i> Partial Success
          </el-tag>
        </div>
      </div>

      <el-row :gutter="20" class="summary-stats">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-number">{{ result.totalCount }}</div>
            <div class="stat-label">Total Records</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card success">
            <div class="stat-number">{{ result.successCount }}</div>
            <div class="stat-label">Successful</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card failed">
            <div class="stat-number">{{ result.failedCount }}</div>
            <div class="stat-label">Failed</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-number">{{ Math.round((result.successCount / result.totalCount) * 100) }}%</div>
            <div class="stat-label">Success Rate</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- Tab Container -->
    <el-tabs v-model="activeTab" type="card" class="result-tabs">

      <!-- Success Tab -->
      <el-tab-pane v-if="result.successCount > 0" name="success">
        <span slot="label">
          <i class="el-icon-circle-check"></i>
          Success ({{ result.successCount }})
        </span>

        <div class="tab-content">
          <!-- Search and Actions for Success -->
          <div class="toolbar">
            <el-input
              v-model="successSearch"
              placeholder="Search tracking numbers..."
              prefix-icon="el-icon-search"
              clearable
              style="width: 300px;">
            </el-input>

            <div style="float: right;">
              <el-button type="success" size="small" @click="exportSuccessData">
                <i class="el-icon-download"></i> Export Success
              </el-button>
            </div>
          </div>

          <el-table
            :data="filteredSuccessData"
            :height="tableHeight"
            stripe
            :empty-text="successSearch ? 'No matching records found' : 'No successful records'">

            <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
            <el-table-column prop="tracking" label="Tracking Number" min-width="200">
              <template slot-scope="scope">
                <el-tag type="success" size="small">{{ scope.row.tracking || scope.row }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="items" label="Items" min-width="300" v-if="result.successDetails">
              <template slot-scope="scope">
                <div v-if="scope.row.items">
                  <el-tag
                    v-for="item in scope.row.items.slice(0, 3)"
                    :key="item.upc"
                    size="mini"
                    style="margin-right: 5px; margin-bottom: 2px;">
                    {{ item.upc }} ({{ item.quantity }})
                  </el-tag>
                  <el-tag v-if="scope.row.items.length > 3" type="info" size="mini">
                    +{{ scope.row.items.length - 3 }} more
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="description" label="Description" show-overflow-tooltip v-if="result.successDetails">
            </el-table-column>
          </el-table>

          <!-- Success Pagination -->
          <el-pagination
            v-if="successData.length > successPageSize"
            @current-change="handleSuccessPageChange"
            :current-page="successCurrentPage"
            :page-size="successPageSize"
            layout="total, prev, pager, next"
            :total="successData.length"
            style="margin-top: 20px; text-align: center;">
          </el-pagination>
        </div>
      </el-tab-pane>

      <!-- Failed Tab -->
      <el-tab-pane v-if="result.failedCount > 0" name="failed">
        <span slot="label">
          <i class="el-icon-circle-close"></i>
          Failed ({{ result.failedCount }})
        </span>

        <div class="tab-content">
          <!-- Search and Filter for Failed -->
          <div class="toolbar">
            <el-input
              v-model="failedSearch"
              placeholder="Search content or error messages..."
              prefix-icon="el-icon-search"
              clearable
              style="width: 300px;">
            </el-input>

            <el-select
              v-model="errorTypeFilter"
              placeholder="Filter by error type"
              clearable
              style="width: 200px; margin-left: 10px;">
              <el-option
                v-for="errorType in uniqueErrorTypes"
                :key="errorType"
                :label="errorType"
                :value="errorType">
              </el-option>
            </el-select>

            <div style="float: right;">
              <el-button type="danger" size="small" @click="exportFailedData">
                <i class="el-icon-download"></i> Export Failed
              </el-button>
              <el-button type="warning" size="small" @click="copyFailedData">
                <i class="el-icon-document-copy"></i> Copy for Retry
              </el-button>
            </div>
          </div>

          <el-table
            :data="filteredFailedData"
            :height="tableHeight"
            stripe
            :empty-text="failedSearch || errorTypeFilter ? 'No matching records found' : 'No failed records'">

            <el-table-column type="index" label="#" width="50" align="center"></el-table-column>

            <el-table-column label="Line" width="80" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.lineNumber || scope.$index + 1 }}</span>
              </template>
            </el-table-column>

            <el-table-column label="Error Message" show-overflow-tooltip>
              <template slot-scope="scope">
                <div class="error-message-container">
                  <span class="error-message-text">
                    {{ getDisplayErrorMessage(scope.row) }}
                  </span>
                  <el-button
                    size="mini"
                    type="text"
                    icon="el-icon-document"
                    @click="showFullErrorMessage(scope.row)"
                    title="View full error message"
                    class="error-detail-btn">
                  </el-button>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="Actions" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  type="text"
                  @click="copyToClipboard(scope.row.rawContent || scope.row.content)"
                  title="Copy to clipboard">
                  <i class="el-icon-document-copy"></i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- Failed Pagination -->
          <el-pagination
            v-if="failedData.length > failedPageSize"
            @current-change="handleFailedPageChange"
            :current-page="failedCurrentPage"
            :page-size="failedPageSize"
            layout="total, prev, pager, next"
            :total="failedData.length"
            style="margin-top: 20px; text-align: center;">
          </el-pagination>
        </div>
      </el-tab-pane>

    </el-tabs>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Close</el-button>
      <el-button v-if="result.failedCount > 0" type="warning" @click="handleRetryFailed">
        <i class="el-icon-refresh"></i> Retry {{ result.failedCount }} Failed Record(s)
      </el-button>
      <el-button v-if="result.successCount > 0" type="primary" @click="handleContinue">
        <i class="el-icon-check"></i> Continue Import Process
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'ImportResultDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    result: {
      type: Object,
      required: true,
      default: () => ({
        totalCount: 0,
        successCount: 0,
        failedCount: 0,
        successTrackings: [],
        successDetails: null,
        failedRecords: []
      })
    }
  },

  data() {
    return {
      dialogVisible: false,
      activeTab: 'success',

      // Search and filter
      successSearch: '',
      failedSearch: '',
      errorTypeFilter: '',

      // Pagination
      successCurrentPage: 1,
      successPageSize: 20,
      failedCurrentPage: 1,
      failedPageSize: 20
    }
  },

  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.resetFilters()
        // Default to failed tab if there are failures
        this.activeTab = this.result.failedCount > 0 ? 'failed' : 'success'
      }
    },

    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },

  computed: {
    successData() {
      if (this.result.successDetails) {
        return this.result.successDetails
      }
      return (this.result.successTrackings || []).map(tracking => ({ tracking }))
    },

    failedData() {
      return this.result.failedRecords || []
    },

    filteredSuccessData() {
      let data = this.successData

      if (this.successSearch) {
        const search = this.successSearch.toLowerCase()
        data = data.filter(item => {
          const tracking = (item.tracking || item || '').toString().toLowerCase()
          return tracking.includes(search)
        })
      }

      // Apply pagination
      const start = (this.successCurrentPage - 1) * this.successPageSize
      const end = start + this.successPageSize
      return data.slice(start, end)
    },

    filteredFailedData() {
      let data = this.failedData

      if (this.failedSearch) {
        const search = this.failedSearch.toLowerCase()
        data = data.filter(item => {
          const content = (item.rawContent || item.content || '').toString().toLowerCase()
          const error = (item.errorMessage || item.error || '').toString().toLowerCase()
          return content.includes(search) || error.includes(search)
        })
      }

      if (this.errorTypeFilter) {
        data = data.filter(item => {
          const error = item.errorMessage || item.error || ''
          return error.includes(this.errorTypeFilter)
        })
      }

      // Apply pagination
      const start = (this.failedCurrentPage - 1) * this.failedPageSize
      const end = start + this.failedPageSize
      return data.slice(start, end)
    },

    uniqueErrorTypes() {
      const errorTypes = new Set()
      this.failedData.forEach(item => {
        const error = item.errorMessage || item.error || ''
        if (error) {
          // Extract common error patterns
          if (error.includes('duplicate')) errorTypes.add('Duplicate')
          else if (error.includes('format')) errorTypes.add('Format Error')
          else if (error.includes('validation')) errorTypes.add('Validation Error')
          else if (error.includes('not found')) errorTypes.add('Not Found')
          else if (error.includes('length')) errorTypes.add('Length Error')
          else errorTypes.add('Other')
        }
      })
      return Array.from(errorTypes)
    },

    // 动态计算表格高度
    tableHeight() {
      const currentTabData = this.activeTab === 'success' ? this.successData : this.failedData
      const recordCount = currentTabData.length

      // 基础高度：150px，每条记录增加40px，最小200px，最大500px
      const minHeight = 200
      const maxHeight = 500
      const baseHeight = 150
      const rowHeight = 40

      const calculatedHeight = Math.min(
        Math.max(minHeight, baseHeight + (recordCount * rowHeight)),
        maxHeight
      )

      return calculatedHeight
    }
  },

  methods: {
    resetFilters() {
      this.successSearch = ''
      this.failedSearch = ''
      this.errorTypeFilter = ''
      this.successCurrentPage = 1
      this.failedCurrentPage = 1
    },

    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    },

    handleContinue() {
      this.$emit('continue', this.result)
      this.handleClose()
    },

    handleRetryFailed() {
      // 准备失败记录的重试数据
      const retryData = this.failedData.map(record =>
        record.rawContent || record.content || ''
      ).filter(content => content.trim().length > 0).join('\n')

      this.$emit('retry-failed', {
        failedRecords: this.failedData,
        retryData: retryData,
        failedCount: this.result.failedCount
      })
      this.handleClose()
    },

    handleSuccessPageChange(page) {
      this.successCurrentPage = page
    },

    handleFailedPageChange(page) {
      this.failedCurrentPage = page
    },

    getShortErrorMessage(error) {
      if (!error) return 'Unknown error'
      if (error.length <= 50) return error
      return error.substring(0, 47) + '...'
    },

    showFullErrorMessage(row) {
      const content = row.rawContent || row.content || 'No content'
      const error = row.errorMessage || row.error || 'Unknown error'
      const lineNumber = row.lineNumber || 'Unknown'

      this.$msgbox({
        title: `Error Details - Line ${lineNumber}`,
        message: `
          <div style="text-align: left;">
            <div style="margin-bottom: 15px;">
              <strong>Content:</strong>
              <div style="background: #f5f5f5; padding: 10px; border-radius: 4px; margin-top: 5px; font-family: monospace; word-break: break-all;">
                ${content}
              </div>
            </div>
            <div>
              <strong>Error Message:</strong>
              <div style="background: #fef0f0; padding: 10px; border-radius: 4px; margin-top: 5px; color: #f56c6c; border-left: 4px solid #f56c6c;">
                ${error}
              </div>
            </div>
          </div>
        `,
        dangerouslyUseHTMLString: true,
        confirmButtonText: 'Close',
        showCancelButton: true,
        cancelButtonText: 'Copy Content',
        beforeClose: (action, instance, done) => {
          if (action === 'cancel') {
            this.copyToClipboard(content)
          }
          done()
        }
      }).catch(() => {
        // User closed dialog
      })
    },

    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('Copied to clipboard')
        }).catch(() => {
          this.fallbackCopyToClipboard(text)
        })
      } else {
        this.fallbackCopyToClipboard(text)
      }
    },

    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        this.$message.success('Copied to clipboard')
      } catch (err) {
        this.$message.error('Failed to copy to clipboard')
      }
      document.body.removeChild(textArea)
    },

    copyFailedData() {
      const failedLines = this.failedData.map(item =>
        item.rawContent || item.content || ''
      ).filter(line => line.trim()).join('\n')

      this.copyToClipboard(failedLines)
    },

    exportSuccessData() {
      const content = this.successData.map(item => {
        if (item.tracking) {
          return item.tracking
        }
        return item
      }).join('\n')

      this.downloadFile(content, 'import_success.txt')
    },

    exportFailedData() {
      const content = this.failedData.map(item => {
        const line = item.rawContent || item.content || ''
        const error = item.errorMessage || item.error || ''
        return `${line}\t// Error: ${error}`
      }).join('\n')

      this.downloadFile(content, 'import_failed.txt')
    },

    downloadFile(content, filename) {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    // 获取用于显示的错误消息
    getDisplayErrorMessage(row) {
      const errorMessage = row.errorMessage || row.error || 'Unknown error'
      const rawContent = row.rawContent || row.content || ''

      // 如果没有原始数据，直接返回错误消息
      if (!rawContent) {
        return errorMessage
      }

      // 清理和格式化原始数据用于显示
      const cleanRawContent = String(rawContent).trim()

      // 如果原始数据太长，截断显示
      const displayContent = cleanRawContent.length > 80
        ? cleanRawContent.substring(0, 77) + '...'
        : cleanRawContent

      // 特殊处理不同类型的错误
      if (errorMessage.includes('格式错误') || errorMessage.includes('应为：') || errorMessage.includes('实际字段数')) {
        return `❌ 格式错误："${displayContent}" (${this.extractFieldCountInfo(errorMessage)})`
      }

      if (errorMessage.includes('UPC') && (errorMessage.includes('不存在') || errorMessage.includes('invalid'))) {
        return `❌ UPC不存在："${displayContent}"`
      }

      if (errorMessage.includes('duplicate') || errorMessage.includes('重复') || errorMessage.includes('已存在')) {
        return `❌ 重复数据："${displayContent}"`
      }

      if (errorMessage.includes('length') || errorMessage.includes('长度')) {
        return `❌ 长度错误："${displayContent}"`
      }

      // 默认显示：数据 + 错误信息
      return `❌ "${displayContent}" - ${errorMessage}`
    },

    // 提取字段数量信息
    extractFieldCountInfo(errorMessage) {
      const match = errorMessage.match(/实际字段数[：:]\s*(\d+)/)
      if (match) {
        return `应为3个字段，实际${match[1]}个`
      }
      return '字段数量不正确'
    }
  }
}
</script>

<style scoped>
.import-result-dialog {
  margin-top: 5vh !important;
}

.summary-card {
  margin-bottom: 20px;
}

.summary-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.summary-stats {
  margin-top: 20px;
}

.stat-card {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.stat-card.success {
  background: #f0f9ff;
  border-color: #67c23a;
}

.stat-card.failed {
  background: #fef0f0;
  border-color: #f56c6c;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.result-tabs {
  margin-top: 20px;
}

.tab-content {
  padding: 20px 0;
}

.toolbar {
  margin-bottom: 20px;
  overflow: hidden;
}

.failed-content {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #e74c3c;
  word-break: break-all;
  white-space: pre-wrap;
}

.error-message-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.error-message-text {
  flex: 1;
  color: #f56c6c;
  word-break: break-word;
  line-height: 1.4;
}

.error-detail-btn {
  flex-shrink: 0;
  margin-left: 8px;
}

.dialog-footer {
  text-align: center;
  padding-top: 20px;
}

/* Responsive design */
@media (max-width: 768px) {
  .import-result-dialog {
    width: 98% !important;
    margin: 1vh auto !important;
  }

  .summary-stats .el-col {
    margin-bottom: 15px;
  }

  .toolbar {
    text-align: left;
  }

  .toolbar > div[style*="float: right"] {
    float: none !important;
    margin-top: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// ImportResultDialog.vue?0c2871c0